import middlesexLogoUrl from "@/assets/middlesex-logo.svg";
import derbyLogoUrl from "@/assets/derby-logo.svg";

export const FONT_SCALE_OPTIONS = ["sm", "md", "lg", "xl"];
export const ALIGNMENT_OPTIONS = ["left", "center"];
export const MAX_WIDTH_OPTIONS = ["narrow", "normal", "wide"];
export const LOWER_THIRDS_LOGO_PRESET_OPTIONS = ["none", "middlesex", "derby", "custom"];
export const LOWER_THIRDS_LOGO_SIZE_OPTIONS = ["sm", "md", "lg"];
export const DEFAULT_QUEUE_VIEW = {
  seatNumberPill: {
    enabled: false,
    field: "",
    label: "Seat",
  },
};
export const DEFAULT_LOWER_THIRDS_BRANDING = {
  logoPreset: "none",
  logoUrl: "",
  logoSize: "md",
  backgroundColor: "#2f2552",
  textColor: "#ffffff",
  fontFamily: "Arial, sans-serif",
};

export const PRESENTER_PRESETS = {
  default: {
    preset: "default",
    student: {
      headlineField: "fullName",
      sublineField: "presenterPhonetics",
      showTypeLabel: true,
      alignment: "left",
      fontScale: "lg",
      maxWidth: "wide",
    },
    presenterNote: {
      bodyField: "text",
      alignment: "left",
      fontScale: "lg",
      maxWidth: "wide",
    },
    displayLine: {
      bodyField: "text",
      alignment: "center",
      fontScale: "xl",
      maxWidth: "normal",
    },
  },
  minimal: {
    preset: "minimal",
    student: {
      headlineField: "fullName",
      sublineField: "presenterPhonetics",
      showTypeLabel: false,
      alignment: "left",
      fontScale: "md",
      maxWidth: "normal",
    },
    presenterNote: {
      bodyField: "text",
      alignment: "left",
      fontScale: "md",
      maxWidth: "normal",
    },
    displayLine: {
      bodyField: "text",
      alignment: "center",
      fontScale: "lg",
      maxWidth: "normal",
    },
  },
  narrative: {
    preset: "narrative",
    student: {
      headlineField: "fullName",
      sublineField: "presenterPhonetics",
      showTypeLabel: false,
      alignment: "center",
      fontScale: "lg",
      maxWidth: "normal",
    },
    presenterNote: {
      bodyField: "text",
      alignment: "center",
      fontScale: "xl",
      maxWidth: "wide",
    },
    displayLine: {
      bodyField: "text",
      alignment: "center",
      fontScale: "xl",
      maxWidth: "wide",
    },
  },
};

export const LOWER_THIRDS_PRESETS = {
  default: {
    preset: "default",
    student: {
      primaryField: "fullName",
      secondaryField: "award",
      alignment: "left",
      fontScale: "lg",
      showSecondaryLine: true,
    },
    displayLine: {
      headlineField: "text",
      alignment: "center",
      fontScale: "xl",
    },
  },
  minimal: {
    preset: "minimal",
    student: {
      primaryField: "fullName",
      secondaryField: "award",
      alignment: "left",
      fontScale: "md",
      showSecondaryLine: true,
    },
    displayLine: {
      headlineField: "text",
      alignment: "center",
      fontScale: "lg",
    },
  },
  headline: {
    preset: "headline",
    student: {
      primaryField: "fullName",
      secondaryField: "award",
      alignment: "center",
      fontScale: "xl",
      showSecondaryLine: false,
    },
    displayLine: {
      headlineField: "text",
      alignment: "center",
      fontScale: "xl",
    },
  },
};

export const PRESENTER_PRESET_OPTIONS = Object.keys(PRESENTER_PRESETS);
export const LOWER_THIRDS_PRESET_OPTIONS = Object.keys(LOWER_THIRDS_PRESETS);

export const DEFAULT_VIEWS = {
  queue: clonePreset(DEFAULT_QUEUE_VIEW),
  presenter: clonePreset(PRESENTER_PRESETS.default),
  lowerThirds: clonePreset(LOWER_THIRDS_PRESETS.default),
};

export function cloneViews(views, event = null) {
  const normalizedViews = normalizeViews(views);
  applyUniversityLogoDefaults(normalizedViews, event);
  return JSON.parse(JSON.stringify(normalizedViews));
}

export function clonePresenterPreset(preset) {
  return clonePreset(PRESENTER_PRESETS[preset] || PRESENTER_PRESETS.default);
}

export function cloneLowerThirdsPreset(preset) {
  return {
    ...clonePreset(LOWER_THIRDS_PRESETS[preset] || LOWER_THIRDS_PRESETS.default),
    branding: clonePreset(DEFAULT_LOWER_THIRDS_BRANDING),
  };
}

export function resolveDisplayValue(item, fieldName) {
  const normalizedField = (fieldName || "").trim();
  if (!normalizedField || !item) {
    return "";
  }

  if (normalizedField === "presenterPhonetics") {
    return (
      item.presenterPhonetics ||
      item.oratorNotes ||
      item.pronunciation?.manualPhonetics ||
      item.pronunciation?.activePhonetics ||
      item.pronunciation?.phonetics ||
      ""
    );
  }

  if (normalizedField === "oratorNotes") {
    return item.oratorNotes || "";
  }

  if (normalizedField === "activePhonetics") {
    return (
      item.pronunciation?.manualPhonetics ||
      item.pronunciation?.activePhonetics ||
      item.pronunciation?.phonetics ||
      ""
    );
  }

  if (normalizedField === "manualPhonetics") {
    return item.pronunciation?.manualPhonetics || "";
  }

  if (["fullName", "award", "text", "type"].includes(normalizedField)) {
    return item[normalizedField] || "";
  }

  return item.extraFields?.[normalizedField] || "";
}

export function buildFieldOptions(snapshot) {
  const options = [
    { value: "fullName", label: "Full Name" },
    { value: "award", label: "Award" },
    { value: "text", label: "Text" },
    { value: "presenterPhonetics", label: "Orator Notes / Phonetics" },
    { value: "oratorNotes", label: "Orator Notes" },
    { value: "activePhonetics", label: "Active Phonetics" },
    { value: "manualPhonetics", label: "Manual Phonetics" },
  ];
  const seen = new Set(options.map((option) => option.value));
  const columns = snapshot?.participants?.columns || [];

  columns.forEach((column) => {
    if (!seen.has(column)) {
      seen.add(column);
      options.push({ value: column, label: column });
    }
  });

  return options;
}

export function buildQueueFieldOptions(snapshot) {
  return (snapshot?.participants?.columns || []).map((column) => ({
    value: column,
    label: column,
  }));
}

export function suggestSeatNumberField(options = []) {
  const preferredFields = [
    "studentseatnumber",
    "studentseatnumbe",
    "seatnumber",
    "seatno",
    "seat",
    "cardnumber",
    "cardno",
  ];
  const normalizedOptions = options.map((option) => ({
    ...option,
    normalizedValue: `${option.value || ""}`.toLowerCase().replace(/[^a-z0-9]/g, ""),
  }));

  for (const preferredField of preferredFields) {
    const match = normalizedOptions.find((option) => option.normalizedValue === preferredField);
    if (match) {
      return match.value;
    }
  }
  return "";
}

export function presenterFontStyles(scale) {
  const sizeMap = {
    sm: { headline: "2.5rem", subline: "1.5rem", body: "1.75rem" },
    md: { headline: "3.2rem", subline: "1.9rem", body: "2.2rem" },
    lg: { headline: "4rem", subline: "2.25rem", body: "3rem" },
    xl: { headline: "4.8rem", subline: "2.7rem", body: "3.6rem" },
  };
  return sizeMap[scale] || sizeMap.lg;
}

export function presenterWidthValue(width) {
  const widthMap = {
    narrow: "56rem",
    normal: "68rem",
    wide: "82rem",
  };
  return widthMap[width] || widthMap.wide;
}

export function lowerThirdsFontStyles(scale) {
  const sizeMap = {
    sm: { primary: "4rem", secondary: "2rem", headline: "3.2rem" },
    md: { primary: "5rem", secondary: "2.5rem", headline: "4rem" },
    lg: { primary: "6rem", secondary: "3rem", headline: "4.6rem" },
    xl: { primary: "6.6rem", secondary: "3.3rem", headline: "5.2rem" },
  };
  return sizeMap[scale] || sizeMap.lg;
}

export function resolveLowerThirdsLogoUrl(branding = {}) {
  const logoPreset = (branding.logoPreset || "").trim().toLowerCase();
  if (logoPreset === "middlesex") {
    return middlesexLogoUrl;
  }
  if (logoPreset === "derby") {
    return derbyLogoUrl;
  }
  if (logoPreset === "custom" || (!logoPreset && branding.logoUrl)) {
    return (branding.logoUrl || "").trim();
  }
  return "";
}

export function lowerThirdsBrandingStyle(branding = {}) {
  const normalizedBranding = {
    ...DEFAULT_LOWER_THIRDS_BRANDING,
    ...(branding || {}),
  };
  return {
    backgroundColor: normalizedBranding.backgroundColor,
    color: normalizedBranding.textColor,
    fontFamily: normalizedBranding.fontFamily,
  };
}

export function lowerThirdsLogoStyle(branding = {}) {
  const normalizedBranding = {
    ...DEFAULT_LOWER_THIRDS_BRANDING,
    ...(branding || {}),
  };
  const sizes = {
    sm: { height: "56%", padding: "0.35em 0" },
    md: { height: "72%", padding: "0.28em 0" },
    lg: { height: "88%", padding: "0.18em 0" },
  };
  return sizes[normalizedBranding.logoSize] || sizes.md;
}

function clonePreset(value) {
  return JSON.parse(JSON.stringify(value));
}

function normalizeViews(views) {
  const source = views || {};
  return {
    queue: {
      ...clonePreset(DEFAULT_QUEUE_VIEW),
      ...(source.queue || {}),
      seatNumberPill: {
        ...clonePreset(DEFAULT_QUEUE_VIEW.seatNumberPill),
        ...(source.queue?.seatNumberPill || {}),
      },
    },
    presenter: {
      ...clonePresenterPreset(source.presenter?.preset),
      ...(source.presenter || {}),
      student: {
        ...clonePresenterPreset(source.presenter?.preset).student,
        ...(source.presenter?.student || {}),
      },
      presenterNote: {
        ...clonePresenterPreset(source.presenter?.preset).presenterNote,
        ...(source.presenter?.presenterNote || {}),
      },
      displayLine: {
        ...clonePresenterPreset(source.presenter?.preset).displayLine,
        ...(source.presenter?.displayLine || {}),
      },
    },
    lowerThirds: {
      ...cloneLowerThirdsPreset((source.lowerThirds || source.vmix)?.preset),
      ...(source.lowerThirds || source.vmix || {}),
      branding: normalizeLowerThirdsBranding((source.lowerThirds || source.vmix)?.branding),
      student: {
        ...cloneLowerThirdsPreset((source.lowerThirds || source.vmix)?.preset).student,
        ...((source.lowerThirds || source.vmix)?.student || {}),
      },
      displayLine: {
        ...cloneLowerThirdsPreset((source.lowerThirds || source.vmix)?.preset).displayLine,
        ...((source.lowerThirds || source.vmix)?.displayLine || {}),
      },
    },
  };
}

function normalizeLowerThirdsBranding(branding) {
  const normalizedBranding = {
    ...DEFAULT_LOWER_THIRDS_BRANDING,
    ...(branding || {}),
  };
  if (!branding?.logoPreset && branding?.logoUrl) {
    normalizedBranding.logoPreset = "custom";
  }
  if (!LOWER_THIRDS_LOGO_SIZE_OPTIONS.includes(normalizedBranding.logoSize)) {
    normalizedBranding.logoSize = DEFAULT_LOWER_THIRDS_BRANDING.logoSize;
  }
  return normalizedBranding;
}

function applyUniversityLogoDefaults(views, event) {
  const universitySlug = (event?.universitySlug || "").trim().toLowerCase();
  const branding = views?.lowerThirds?.branding;
  if (!branding) {
    return;
  }
  if (universitySlug === "derby" && branding.logoPreset === "none" && !branding.logoUrl) {
    branding.logoPreset = "derby";
  }
}
